import { useEffect, useRef } from "react";
import Chart from "react-apexcharts";

const DonutsUsageInmarsat = ({
	allowance,
	totalUsage = 0,
	unity,
	message,
	AllowanceValueName,
	AllowanceCurrentValueName,
}) => {
	const containerRef = useRef(null);

	const allowanceNum = parseFloat(allowance);
	const hasAllowance = !isNaN(allowanceNum) && allowanceNum > 0;

	const formattedAllowance = hasAllowance ? allowanceNum.toFixed(2) : "0.00";
	const formattedUsage = parseFloat(totalUsage).toFixed(2);

	const usage = formattedUsage > allowanceNum ? allowanceNum : formattedUsage;
	const usagePercentage = ((usage / allowanceNum) * 100).toFixed(2);

	const Remaining = (formattedAllowance - formattedUsage).toFixed(2);
	const formatedRemaining = Remaining < 0 || isNaN(Remaining) ? 0 : Remaining;
	const formatedUsagePercentage = isNaN(usagePercentage) ? 0 : usagePercentage;

	const allowanceGb = allowance === undefined ? `0 ${unity}` : `${allowance} ${unity}`;

	// nudge the usage color to an amber warning once someone's close to their cap
	const isNearLimit = hasAllowance && parseFloat(formatedUsagePercentage) >= 90;
	const usageColor = isNearLimit ? "#F59E0B" : "#0E9F6E";

	const options = {
		chart: {
			type: "donut",
		},
		labels: ["Usage", "Remaining"],
		colors: [usageColor, "#1B204C"],
		plotOptions: {
			pie: {
				donut: {
					size: "65%",
					labels: {
						show: false, // percentage is rendered via the overlay div, not Apex's own label
					},
				},
			},
		},
		stroke: {
			width: 0.5,
			colors: ["transparent"],
		},
		dataLabels: {
			enabled: false,
		},
		legend: {
			position: "bottom",
			horizontalAlign: "center",
			fontSize: "11px",
			itemMargin: { horizontal: 7, vertical: 3 },
			labels: {
				colors: ["#9CA3FF"], // dimmed base — CSS hover above brightens to white on interaction
			},
		},
		tooltip: {
			theme: "dark",
			style: {
				fontSize: "12px",
				fontFamily: "Arial, sans-serif",
			},
			y: {
				formatter: (val) => `${val.toFixed(2)} ${unity}`,
			},
			custom: ({
				series,
				seriesIndex,
				w,
			}) => `<div style="background:#272B49 ; color: #FFFFFF; padding: 8px; border-radius: 6px;">
                  <span class="font-semibold text-base leading-normal tracking-normal" style="font-family: 'Segoe UI', sans-serif; color:#9CA3AF"  >${w.globals.labels[seriesIndex]}:</span>  <span  class="font-bold text-sm leading-none tracking-normal" style="font-family: 'Segoe UI', sans-serif;" > ${series[seriesIndex]}%</span>
                </div>`,
		},
		responsive: [
			{
				breakpoint: 480,
				options: {
					legend: { fontSize: "9px", itemMargin: { horizontal: 4, vertical: 2 } },
				},
			},
		],
	};

	const series = hasAllowance
		? [parseFloat(formatedUsagePercentage), 100 - parseFloat(formatedUsagePercentage)]
		: [0, 1]; // renders an empty ring rather than a misleading full "0% used" ring

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const handleOver = (e) => {
			if (e.target.classList?.contains("apexcharts-legend-text")) {
				e.target.style.setProperty("color", "#B6C9D2", "important");
			}
		};
		const handleOut = (e) => {
			if (e.target.classList?.contains("apexcharts-legend-text")) {
				e.target.style.setProperty("color", "#9CA3AF", "important");
			}
		};

		container.addEventListener("mouseover", handleOver);
		container.addEventListener("mouseout", handleOut);
		return () => {
			container.removeEventListener("mouseover", handleOver);
			container.removeEventListener("mouseout", handleOut);
		};
	}, []);
	return (
		<div ref={containerRef} className="mb-1 rounded-lg">
			<div className="col-span-1">
				<div className="relative mx-auto aspect-square w-full max-w-[160px] sm:max-w-[180px]">
					<Chart options={options} series={series} type="donut" width="100%" height="100%" />
					<div
						className="pointer-events-none absolute inset-0 flex items-center justify-center"
						style={{ paddingBottom: "20%" }}
					>
						<span
							className="font-bold"
							style={{
								fontSize: "clamp(10px, 2vw, 18px)",
								color: isNearLimit ? "#F59E0B" : "#ffffff",
							}}
						>
							{formatedUsagePercentage}%
						</span>
					</div>
				</div>
				<div className="mt-2 text-center text-[11px] text-gray-400">
					{formattedUsage} / {formattedAllowance} {unity} used
				</div>
			</div>
			<div className="col-span-2 mt-4 space-y-3 rounded-lg bg-blue-gray bg-opacity-20 p-2">
				<div className="flex items-center justify-between">
					<span className="font-normal text-[10px] text-gray-300 sm:text-xs">
						{AllowanceValueName}
					</span>
					<span className="rounded bg-blue-900/80 px-2.5 py-0.5 font-medium text-[10px] text-blue-200 sm:text-xs">
						{formattedAllowance}
					</span>
				</div>

				<div className="flex items-center justify-between">
					<span className="font-normal text-[10px] text-gray-300 sm:text-xs">
						{AllowanceCurrentValueName}
					</span>
					<span className="rounded bg-green-500/80 px-2.5 py-0.5 font-medium text-[10px] text-blue-200 sm:text-xs">
						{parseFloat(formattedUsage)} {unity}
					</span>
				</div>
			</div>
		</div>
	);
};

export default DonutsUsageInmarsat;
